import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller } from "react-hook-form";
import { Typography } from "@mui/material";
import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { parseISO } from "date-fns";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import { InputController } from "@shared/ui/form";
import InputDatePickerController from "@shared/ui/form/component/InputDatePickerController";
import { SelectMultiple } from "@vendor/examples/SelectMultiple";
import MDButton from "@vendor/components/MDButton";
import { buildUpdateProfileSchema } from "../schema/updateProfileSchema";
import { useProfileFieldsMetadata } from "../hooks/useProfileFieldsMetadata";
import { useUpdateProfile } from "../hooks/useUpdateProfile";
import { SWAL_CONFIRM_COLOR } from "../constants";
import { buildProfileUpdatePayload, mapProfileMetadata } from "../lib/userProfileRules";

import type { UserProfile } from "../api/userProfileApi";

export interface UpdateProfileProps {
    user?: UserProfile;
    onSuccess?: () => void;
}

export const UpdateProfile = ({ user, onSuccess }: UpdateProfileProps) => {
    const { t } = useTranslation();

    const schema = useMemo(() => buildUpdateProfileSchema(t), [t]);

    const { control, handleSubmit, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
    });

    const [validateProfesionalProfile, setValidateProfesionalProfile] = useState(false);
    const [profesionalProfileSelected, setprofesionalProfileSelected] = useState<unknown[]>([]);
    const [profesionalProfile, setProfesionalProfile] = useState<unknown[]>([]);
    const [countries, setCountries] = useState<unknown[]>([]);
    const [nationalities, setNationalities] = useState<unknown[]>([]);
    const [genres, setGenres] = useState<unknown[]>([]);

    const { data: metadataData } = useProfileFieldsMetadata();
    const { mutate: doUpdateProfile } = useUpdateProfile({ onSuccess });

    useEffect(() => {
        if (!metadataData?.fields) return;

        const metadata = mapProfileMetadata(metadataData.fields);
        setCountries(metadata.countries);
        setProfesionalProfile(metadata.professionalProfile);
        setNationalities(metadata.nationalities);
        setGenres(metadata.genres);
    }, [metadataData]);

    useEffect(() => {
        if (!user) return;

        setValue("firstName", user.firstName ?? "", { shouldDirty: true, shouldValidate: true });
        setValue("lastName", user.lastName ?? "", { shouldDirty: true, shouldValidate: true });
        setValue("country", user.country ?? "", { shouldDirty: true, shouldValidate: true });
        setValue("nationality", user.nationality ?? "", { shouldDirty: true, shouldValidate: true });
        setValue("gender", user.gender ?? "", { shouldDirty: true, shouldValidate: true });
        setValue("email", user.email ?? "");
        setValue("companyEmail", user.companyEmail ?? "");
        setValue("dateOfBirth", parseISO(user.dateOfBirth ?? ""));
        setValue("phoneNumber", user.phoneNumber ?? "");
        setValue("areaCode", user.areaCode ?? "");
        setValue("address", user.address ?? "");
        setValue("passportNumber", user.passportNumber ?? "");
        setValue("postalCode", user.postalCode ?? "");
        setValue("nameEmergencyContact", user.nameEmergencyContact ?? "");
        setValue("relationshipEmergencyContact", user.relationshipEmergencyContact ?? "");
        setValue("phoneNumberEmergencyContact", user.phoneNumberEmergencyContact ?? "");
        setValue("areaCodeEmergencyContact", user.areaCodeEmergencyContact ?? "");
        // OJO: `bankConfirmationLetter` no esta en updateProfileSchema, asi que queda
        // fuera de la forma validada. Se conserva; agregarlo al schema es un cambio aparte.
        setValue("bankConfirmationLetter" as never, (user.bankConfirmationLetter ?? "") as never);
        setprofesionalProfileSelected((user.professionalProfile ?? []) as unknown[]);
    }, [user, setValue]);

    const validateExtraField = () => {
        if (profesionalProfileSelected.length === 0) {
            setValidateProfesionalProfile(true);
            return true;
        }
        return true;
    };

    const onUpdate = (value: Record<string, unknown>) => {
        const title = t("profileUpdate");
        const subtitle = t("doneSuccessfully", { a: "a" });
        const comfirmationSubtitle = t("doYouWantToUpdateThisRecord");
        const textButtonComfirmation = t("update");

        Swal.fire({
            title: title,
            text: comfirmationSubtitle,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: SWAL_CONFIRM_COLOR.CONFIRM,
            cancelButtonColor: SWAL_CONFIRM_COLOR.CANCEL,
            confirmButtonText: textButtonComfirmation,
            cancelButtonText: t("cancel"),
        }).then((res) => {
            if (res.isConfirmed) {
                if (validateExtraField()) {
                    doUpdateProfile(
                        buildProfileUpdatePayload(
                            value,
                            profesionalProfileSelected,
                            user?.currentProject
                        ) as Parameters<typeof doUpdateProfile>[0]
                    );
                }
            }
        });
    };

    const handleChange = (event: { target: { value: unknown; name: string } }) => {
        const { target: { value, name } } = event;
        if (name === "professionalProfile") {
            setprofesionalProfileSelected(
                (typeof value === "string" ? value.split(",") : value) as unknown[]
            );
            setValidateProfesionalProfile(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onUpdate)}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h6">{t("personalInformation")}: </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <InputController name="firstName" control={control} type="text" defaultValue="" label={t("name") + "*"} />
                        {errors.firstName && <span style={{ fontSize: 12, color: "red" }}>{errors.firstName.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <InputController name="lastName" control={control} type="text" defaultValue="" label={t("lastname") + "*"} />
                        {errors.lastName && <span style={{ fontSize: 12, color: "red" }}>{errors.lastName.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <Controller
                            name="gender"
                            control={control}
                            defaultValue=""
                            rules={{ required: true, maxLength: 50 }}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="gender-label">{t("gender") + "*"}</InputLabel>
                                    <Select labelId="gender-label" id="gender-select" label="Age" {...field}>
                                        {genres.map((gender, index) => (
                                            <MenuItem key={index} value={gender as string}>{gender as string}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.nationality && <span style={{ fontSize: 12, color: "red" }}>{errors.nationality.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <InputController name="email" control={control} type="text" defaultValue="" label={t("email") + "*"} />
                        {errors.email && <span style={{ fontSize: 12, color: "red" }}>{errors.email.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <Controller
                            name="nationality"
                            control={control}
                            defaultValue=""
                            rules={{ required: true, maxLength: 50 }}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="nationality-label">{t("nationality") + "*"}</InputLabel>
                                    <Select labelId="nationality-label" id="nationality-select" label="Age" {...field}>
                                        {nationalities.map((nationality, index) => (
                                            <MenuItem key={index} value={nationality as string}>{nationality as string}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.nationality && <span style={{ fontSize: 12, color: "red" }}>{errors.nationality.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <Controller
                            name="country"
                            control={control}
                            defaultValue=""
                            rules={{ required: true, maxLength: 50 }}
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="country-label">{t("countryResidence") + "*"} </InputLabel>
                                    <Select {...field}>
                                        {countries.map((country, index) => (
                                            <MenuItem key={index} value={country as string}>{country as string}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        {errors.country && <span style={{ fontSize: 12, color: "red" }}>{errors.country.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <InputDatePickerController
                            control={control}
                            defaultValue={new Date()}
                            openTo="month"
                            name="dateOfBirth"
                            label={t("birthdate") + "*"}
                            views={["year", "month", "day"]}
                        />
                        {errors.dateOfBirth && <span style={{ fontSize: 12, color: "red" }}>{errors.dateOfBirth.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="areaCode" control={control} type="number" defaultValue="" label={t("areaCode") + "*"} />
                        {errors.areaCode && <span style={{ fontSize: 12, color: "red" }}>{errors.areaCode.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="phoneNumber" control={control} type="number" defaultValue="" label={t("phone") + "*"} />
                        {errors.phoneNumber && <span style={{ fontSize: 12, color: "red" }}>{errors.phoneNumber.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="passportNumber" control={control} type="text" defaultValue="" label={t("passport") + "*"} />
                        {errors.passportNumber && <span style={{ fontSize: 12, color: "red" }}>{errors.passportNumber.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="postalCode" control={control} type="number" defaultValue="" label={t("postalCode") + "*"} />
                        {errors.postalCode && <span style={{ fontSize: 12, color: "red" }}>{errors.postalCode.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="address" control={control} type="text" defaultValue="" label={t("address") + "*"} />
                        {errors.address && <span style={{ fontSize: 12, color: "red" }}>{errors.address.message}</span>}
                    </FormControl>
                </Grid>

                <Grid size={12}>
                    <Typography variant="h6">{t("emergencyContact")}: </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="nameEmergencyContact" control={control} type="text" defaultValue="" label={t("nameEmergencyContact") + "*"} />
                        {errors.nameEmergencyContact && <span style={{ fontSize: 12, color: "red" }}>{errors.nameEmergencyContact.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="relationshipEmergencyContact" control={control} type="text" defaultValue="" label={t("relationshipEmergencyContact") + "*"} />
                        {errors.relationshipEmergencyContact && <span style={{ fontSize: 12, color: "red" }}>{errors.relationshipEmergencyContact.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 2 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="areaCodeEmergencyContact" control={control} type="number" defaultValue="" label={t("codePhoneEmergency") + "*"} />
                        {errors.areaCodeEmergencyContact && <span style={{ fontSize: 12, color: "red" }}>{errors.areaCodeEmergencyContact.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <InputController name="phoneNumberEmergencyContact" control={control} type="number" defaultValue="" label={t("phoneEmergencyContact") + "*"} />
                        {errors.phoneNumberEmergencyContact && <span style={{ fontSize: 12, color: "red" }}>{errors.phoneNumberEmergencyContact.message}</span>}
                    </FormControl>
                </Grid>

                <Grid size={12}>
                    <Typography variant="h6">{t("professionalInformation")}: </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                        <InputController name="companyEmail" control={control} type="text" defaultValue="" label={t("corporateEmail") + "*"} />
                        {errors.companyEmail && <span style={{ fontSize: 12, color: "red" }}>{errors.companyEmail.message}</span>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth sx={{ mt: 0.8 }}>
                        <SelectMultiple
                            inputName="professionalProfile"
                            label={t("professionalProfile")}
                            values={profesionalProfile}
                            onChange={handleChange as never}
                            selectedValue={profesionalProfileSelected}
                        />
                        {validateProfesionalProfile && <span style={{ fontSize: 12, color: "red" }}>{t("isRequired")}</span>}
                    </FormControl>
                </Grid>

                <Grid size={12}>
                    <MDButton variant="gradient" type="submit" color="info" fullWidth>
                        {t("update")}
                    </MDButton>
                </Grid>
            </Grid>
        </form>
    );
};

